import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';

import ClientDashboardV2 from './ClientDashboardV2';
import ClientsHeader from './components/ClientsHeader';
import PestCompanyModal from './PestCompanyModal';

import { ClientsDisplayBodys } from 'components/ClientTable/ClientsDisplayBodys';
import useGetClientsOnMap from 'hooks/Clients/useGetClientsOnMap';
import { useAppSelector } from 'slices';
import ClientDashboard from 'pages/ClientDashboard/ClientDashboard';
import './CombinedClientsDashboard.scss';
import { getPayedTill } from 'api/clients/checkPayedTill';
import useGetClientsPestCompany from 'hooks/Clients/useGetClientsPestCompany';
import { isServiceProviderExistsResponse } from 'api/clients/types';

interface TabPanelProps {
  children?: React.ReactNode;
  tableName: string;
  currentTable: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, currentTable, tableName } = props;

  return (
    <div className="font-poppins" hidden={currentTable !== tableName} id={`Table-${tableName}`}>
      {currentTable === tableName && <>{children}</>}
    </div>
  );
}

const CombinedClientsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [isOpenCreateCompanyModal, setIsOpenCreateCompanyModal] = useState(false);
  const { data, refetch, isRefetching, isLoading } = useGetClientsOnMap();
  const currentBody = useAppSelector(state => state.ClientsBody.currentClientBody);
  const [searchParams] = useSearchParams();
  const isEmailNotification = searchParams.get('type') === 'email-notifications';
  const effectiveBody = isEmailNotification ? ClientsDisplayBodys.ClientsTableDisplay : currentBody;
  const { data: serviceProviderData } = useGetClientsPestCompany();

  const hasWaitedForCompanyRef = useRef(false);
  useEffect(() => {
    const checkServiceProvider = async (data: isServiceProviderExistsResponse) => {
      try {
        const { is_pest_company, is_active, is_admin } = data;

        if (!is_admin && !is_pest_company) {
          setIsOpenCreateCompanyModal(true);
          hasWaitedForCompanyRef.current = true;
        } else if (is_pest_company && !is_active) {
          enqueueSnackbar(t('ClientsDashboard.is_active'), { variant: 'warning', style: { opacity: 0.5 } });

          hasWaitedForCompanyRef.current = true;
        } else if (is_pest_company && is_active) {
          if (hasWaitedForCompanyRef.current) {
            window.location.reload();
            return;
          }

          const { payed_till } = await getPayedTill();
          const payedTillDate = new Date(payed_till);
          const today = new Date();
          const oneWeekFromToday = new Date();
          oneWeekFromToday.setDate(today.getDate() + 7);

          if (payedTillDate < today) {
            window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}/finances/tariffs/`;
          } else if (payedTillDate <= oneWeekFromToday) {
            enqueueSnackbar(t('ClientsDashboard.payment'), { variant: 'warning' });
          }
        }
      } catch (error) {
        console.error('Error fetching service provider status or payed_till:', error);
      }
    };

    // Call the async function
    if (serviceProviderData) {
      checkServiceProvider(serviceProviderData);
    }
  }, [serviceProviderData, t]);

  const AllObjectsInCompany = useMemo(() => {
    if (data) {
      return data.reduce((total, client) => total + (client?.object_list.length || 0), 0);
    } else {
      return null;
    }
  }, [data]);
  return (
    <div className="page-content custom-map-padding">
      {isOpenCreateCompanyModal && <PestCompanyModal setOpenModal={setIsOpenCreateCompanyModal} />}
      {!isOpenCreateCompanyModal && (
        <>
          <div className="card-header">
            <ClientsHeader refetch={refetch} allObjects={AllObjectsInCompany} />
          </div>
          <>
            <CustomTabPanel currentTable={effectiveBody} tableName={ClientsDisplayBodys.ClientsTableDisplay}>
              <ClientDashboard data={data || []} refetch={refetch} isRefetching={isRefetching} />
            </CustomTabPanel>
            <CustomTabPanel currentTable={effectiveBody} tableName={ClientsDisplayBodys.ClientsMapDisplay}>
              <ClientDashboardV2
                data={data || []}
                refetch={refetch}
                isLoading={isLoading}
                isRefetching={isRefetching}
              />
            </CustomTabPanel>
          </>
        </>
      )}
    </div>
  );
};

export default CombinedClientsDashboard;
